import React, { useState, useRef, useEffect } from 'react';

const KNOWLEDGE = [
  { q: ['get started', 'quickstart', 'how to start', 'begin', 'setup', 'getting started'], a: 'To get started with AfriLink:\n\n1. Register at /admin\n2. Create an API app to get your keys\n3. Add the Connect Widget to your frontend\n4. Use your secret key to fetch financial data\n\nCheck the full quickstart guide for code examples.', link: '/docs', section: 'quickstart' },
  { q: ['api key', 'keys', 'public key', 'secret key', 'pk_test', 'sk_test'], a: 'You get two keys when you create an app:\n\n- Public key (pk_test_...) — used in the Connect Widget on your frontend\n- Secret key (sk_test_...) — used for server-side API calls, never expose client-side\n\nCreate keys in the Dashboard under "API keys". The secret key is only shown once — save it immediately.', link: '/docs', section: 'auth' },
  { q: ['connect', 'widget', 'link bank', 'link account', 'bank account', 'connect bank'], a: 'The Connect Widget lets your users link their bank accounts. Add it with just a few lines of code:\n\n1. Include the AfriLink SDK\n2. Initialize with your public key\n3. Call afrilink.open()\n4. Handle the onSuccess callback\n\nSupported banks include GTBank, Zenith, Access, First Bank, UBA, Kuda, and more.', link: '/docs', section: 'connect' },
  { q: ['transaction', 'transactions', 'history', 'bank statement'], a: 'Fetch transactions for any linked account:\n\nGET /api/data/accounts/:id/transactions\n\nSupports filtering by date range (start/end), type (credit/debit), and pagination (limit, page). Returns up to 100 transactions per page.\n\nIn sandbox mode, each linked account comes with 20 pre-seeded Nigerian bank transactions.', link: '/docs', section: 'transactions' },
  { q: ['balance', 'account balance', 'check balance'], a: 'Get the current balance for a linked account:\n\nGET /api/data/accounts/:id/balance\n\nReturns the balance amount and currency (NGN). Requires your secret key in the x-api-key header.', link: '/docs', section: 'accounts' },
  { q: ['bvn', 'bank verification', 'verify bvn'], a: 'Verify a Bank Verification Number (BVN):\n\nPOST /api/identity/bvn\nBody: { "bvn": "22211122233" }\n\nReturns: verified status, full name, date of birth, phone number, gender, and enrollment bank.\n\nTest BVNs: 22211122233, 33322233344, 44433344455', link: '/docs', section: 'kyc' },
  { q: ['nin', 'national id', 'verify nin', 'vnin', 'virtual nin'], a: 'Verify a National Identification Number (NIN) or Virtual NIN:\n\nPOST /api/identity/nin\nBody: { "nin": "12345678901" } or { "vnin": "..." }\n\nReturns: verified status, full name, date of birth, gender, phone, address.\n\nTest NINs: 12345678901, 98765432109', link: '/docs', section: 'kyc' },
  { q: ['cac', 'business verification', 'company', 'rc number', 'business registration'], a: 'Verify a CAC business registration:\n\nPOST /api/identity/cac\nBody: { "rcNumber": "RC1234567" }\n\nReturns: company name, registration date, status, type, address, directors, and shareholders.\n\nTest RC numbers: RC1234567, RC9876543, BN3456789', link: '/docs', section: 'kyb' },
  { q: ['kyc', 'know your customer', 'identity verification', 'verify identity'], a: 'AfriLink offers individual KYC through:\n\n- BVN verification\n- NIN / vNIN verification\n- Document verification (passport, driver\'s license, voter\'s card)\n- Biometric liveness + face matching\n- Address verification\n\nOr run them all at once with the composite KYC endpoint: POST /api/identity/kyc\n\nResults are tiered: TIER_1 (BVN/NIN), TIER_2 (+document), TIER_3 (+biometric).', link: '/docs', section: 'kyc' },
  { q: ['kyb', 'know your business', 'business check'], a: 'Business verification (KYB) includes:\n\n- CAC registration lookup\n- TIN (Tax ID) verification\n- Director ID verification (bulk)\n- Sanctions screening\n\nComposite endpoint: POST /api/identity/kyb\nRuns all checks in one call.', link: '/docs', section: 'kyb' },
  { q: ['sanctions', 'aml', 'pep', 'watchlist', 'screening', 'anti-money laundering'], a: 'Screen individuals or businesses against 1,100+ global watchlists:\n\nPOST /api/identity/sanctions\nBody: { "fullName": "Name", "entityType": "INDIVIDUAL" }\n\nRisk levels: NONE, MEDIUM (watchlist), HIGH (PEP), CRITICAL (sanctions).\n\nIn sandbox, any name containing "Magu" triggers a match.', link: '/docs', section: 'sanctions' },
  { q: ['credit score', 'credit', 'creditworthiness', 'lending'], a: 'AfriLink provides AI-powered credit scoring based on cash flow analysis:\n\nGET /api/intelligence/:accountId/credit\n\nAnalyzes transaction patterns, income stability, debt-to-income ratio, and spending behavior to produce a score from 0-100 with risk factors.\n\nGreat for lending decisions without traditional credit bureau data.', link: '/docs', section: 'intelligence' },
  { q: ['fraud', 'fraud detection', 'risk', 'anomaly', 'suspicious'], a: 'Fraud detection and anomaly analysis:\n\nGET /api/intelligence/:accountId/fraud — risk score with factors\nGET /api/intelligence/:accountId/anomalies — unusual transaction patterns\n\nDetects velocity anomalies, unusual amounts, account behavior patterns, and more.', link: '/docs', section: 'intelligence' },
  { q: ['income', 'salary', 'income verification', 'income analysis'], a: 'Income analysis powered by transaction data:\n\nGET /api/intelligence/:accountId/income\n\nDetects and categorizes income sources: salary, freelance, pension, government payments. Returns monthly averages, estimated annual income, and stability score.', link: '/docs', section: 'intelligence' },
  { q: ['webhook', 'webhooks', 'events', 'notifications', 'real-time'], a: 'Webhooks deliver real-time notifications via HMAC-signed HTTP POST:\n\nEvents: account.connected, account.updated, account.revoked, identity.verified, data.available\n\nCreate: POST /api/webhooks\nRetry policy: exponential backoff (1m, 5m, 30m, 2h, 24h)\nMax 5 attempts per delivery.', link: '/docs', section: 'webhooks' },
  { q: ['pricing', 'plans', 'cost', 'how much', 'free', 'price'], a: 'AfriLink pricing tiers:\n\n- Starter (Free): 500 API calls, 50 identity checks, 10 intelligence\n- Growth (N25,000/mo): 10,000 calls, 500 identity, 100 intelligence\n- Scale (N100,000/mo): 100,000 calls, 5,000 identity, 1,000 intelligence\n- Enterprise (Custom): unlimited everything\n\nAll sandbox API calls are free.', link: '/#pricing', section: '' },
  { q: ['sandbox', 'test', 'testing', 'test mode', 'mock', 'demo'], a: 'AfriLink runs in sandbox mode by default. All test data is pre-seeded:\n\n- 5 test banks with different balances\n- 3 test BVNs, 2 test NINs\n- 3 test CAC numbers, 2 test TINs\n- 20 seeded transactions per linked account\n\nNo charges in sandbox mode. Switch to live by updating your environment variables.', link: '/docs', section: 'sandbox' },
  { q: ['rate limit', 'throttle', 'too many requests', '429'], a: 'Rate limits by environment:\n\n- Test: 30 requests/minute\n- Live: 120 requests/minute\n\nHeaders: X-RateLimit-Limit, X-RateLimit-Remaining, X-RateLimit-Reset\n\nExceeding your plan quota returns 429 with upgrade instructions.', link: '/docs', section: 'rate-limits' },
  { q: ['countries', 'coverage', 'nigeria', 'ghana', 'kenya', 'south africa', 'where'], a: 'AfriLink currently supports:\n\n- Nigeria (live)\n- Ghana (ready)\n- Kenya (ready)\n- South Africa (ready)\n- Egypt (coming soon)\n- Tanzania (coming soon)\n\nBank data via Mono, identity verification via Smile ID.', link: '/#coverage', section: '' },
  { q: ['error', 'errors', 'not working', 'failed', 'bug', 'issue'], a: 'Common error codes:\n\n- 400: Bad request (check validation errors)\n- 401: No API key provided\n- 403: Invalid/revoked key or session\n- 404: Resource not found\n- 429: Rate limit or quota exceeded\n- 500: Server error\n\nAll errors return a JSON body with an "error" field. Check the error handling docs for details.', link: '/docs', section: 'errors' },
  { q: ['continue chatting', 'continue', 'keep going', 'yes continue'], a: "Great! What else can I help you with?", options: ['How do I get started?', 'How do I verify a BVN?', 'What are the pricing plans?', 'Talk to a human'], link: '', section: '' },
  { q: ['close chat', 'close', 'end chat', 'no thanks', 'nothing else'], a: "Thanks for chatting! This session is now closed. Click the chat icon anytime you need help again.", link: '', section: '', closeChat: true },
  { q: ['live agent', 'human', 'talk to a human', 'speak to someone', 'real person', 'agent'], a: 'Connecting you to a live agent now...', link: '', section: '', liveAgent: true },
  { q: ['support', 'help', 'contact', 'email', 'talk to someone'], a: 'Need help? Here are your options:\n\n- Documentation: /docs\n- Email: support@afrilink.io\n- Dashboard: /admin\n\nFor sandbox testing issues, check the Sandbox Testing guide in our docs.', link: '/docs', section: 'overview' },
];



function findAnswer(input) {
  const lower = input.toLowerCase();
  let bestMatch = null;
  let bestScore = 0;

  for (const item of KNOWLEDGE) {
    let score = 0;
    for (const keyword of item.q) {
      if (lower.includes(keyword)) {
        score += keyword.split(' ').length;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      bestMatch = item;
    }
  }

  if (bestMatch && bestScore > 0) {
    return bestMatch;
  }

  // Greetings
  if (['hello', 'hi', 'hey', 'yo', 'sup', 'good morning', 'good afternoon', 'good evening', 'whats up', 'howdy'].some(function(g) { return lower === g || lower === g + '!' || lower.startsWith(g + ' '); })) {
    return {
      a: "Hey there! How can I help you today?",
      options: ['How do I get started?', 'How does the Connect Widget work?', 'How do I verify a BVN?', 'What are the pricing plans?', 'Talk to a human'],
      link: '', section: '',
    };
  }

  // Thanks / positive
  if (['thanks', 'thank you', 'cool', 'great', 'awesome', 'nice', 'ok', 'okay', 'got it'].some(function(g) { return lower === g || lower === g + '!'; })) {
    return {
      a: "You're welcome! Anything else I can help with?",
      options: ['How do I get started?', 'How do I verify a BVN?', 'What are the pricing plans?', 'Talk to a human'],
      link: '', section: '',
    };
  }

  // Bye
  if (['bye', 'goodbye', 'see you', 'later', 'cya'].some(function(g) { return lower === g || lower === g + '!'; })) {
    return {
      a: "Goodbye! Come back anytime you need help with AfriLink.",
      link: '', section: '',
    };
  }

  // Everything else - friendly fallback with options
  return {
    a: "I didn't quite catch that. Here are some things I can help with:",
    options: ['How do I get started?', 'How does the Connect Widget work?', 'How do I verify a BVN?', 'What are the pricing plans?', 'How do I test in sandbox?', 'What countries are supported?', 'Talk to a human'],
    link: '/docs',
    section: 'overview',
  };
}

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'bot', text: "Hey! I'm AfriLink's assistant. What can I help you with?", options: ['How do I get started?', 'How does the Connect Widget work?', 'How do I verify a BVN?', 'What are the pricing plans?', 'How do I test in sandbox?', 'Talk to a human'] },
  ]);
  const [input, setInput] = useState('');
  const [chatFile, setChatFile] = useState(null);
  const [liveSession, setLiveSession] = useState(null);
  const [polling, setPolling] = useState(false);
  const [chatDragOver, setChatDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const broadcastRef = useRef(null);
  const idleTimerRef = useRef(null);
  const API = window.location.hostname === 'localhost' ? 'http://localhost:4000' : 'https://api.afrilink.io';

  const getUserId = () => localStorage.getItem('afrilink_user_email') || 'anonymous_' + (localStorage.getItem('afrilink_chat_id') || (() => { const id = Math.random().toString(36).slice(2, 10); localStorage.setItem('afrilink_chat_id', id); return id; })());

  // Load transcript on mount
  useEffect(() => {
    const saved = localStorage.getItem('afrilink_chat_messages');
    if (saved) {
      try { setMessages(JSON.parse(saved)); } catch(e) {}
    }
    // Also fetch from backend
    fetch(API + '/api/chat/transcript?userId=' + getUserId())
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data && Array.isArray(data.data) && data.data.length > 0) {
          setMessages(data.data);
          localStorage.setItem('afrilink_chat_messages', JSON.stringify(data.data));
        }
      })
      .catch(() => {});
  }, []);

  // Idle timer - prompt after 2 minutes of silence
  useEffect(() => {
    if (!isOpen || liveSession) return;
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    
    const lastMsg = messages[messages.length - 1];
    // Skip if last message is already the idle prompt or auto-close
    if (lastMsg && lastMsg.text && (
      lastMsg.text.includes('Still there?') || 
      lastMsg.text.includes('Closing chat due to inactivity')
    )) return;

    // Only set timer if last message was from bot (waiting for user)
    if (lastMsg && lastMsg.role === 'bot' && messages.length > 1) {
      idleTimerRef.current = setTimeout(() => {
        setMessages(prev => [...prev, {
          role: 'bot',
          text: "Still there? Would you like to continue, or can I close this chat?",
          options: ['Continue chatting', 'Close chat', 'Talk to a human'],
        }]);
      }, 120000); // 2 minutes
    }
    return () => { if (idleTimerRef.current) clearTimeout(idleTimerRef.current); };
  }, [messages, isOpen, liveSession]);

  // Auto-close after idle prompt is ignored for 2 more minutes
  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    if (lastMsg && lastMsg.text && lastMsg.text.includes('Still there?')) {
      const autoCloseTimer = setTimeout(() => {
        setMessages(prev => [...prev, {
          role: 'bot',
          text: "Closing chat due to inactivity. Click the chat icon anytime to start again!",
        }]);
        setTimeout(() => setIsOpen(false), 3000);
      }, 120000);
      return () => clearTimeout(autoCloseTimer);
    }
  }, [messages]);

  // Save messages on every change
  useEffect(() => {
    if (messages.length > 1) {
      localStorage.setItem('afrilink_chat_messages', JSON.stringify(messages));
      // Sync to backend (debounced)
      const timer = setTimeout(() => {
        fetch(API + '/api/chat/transcript', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: getUserId(), messages }),
        }).catch(() => {});
      }, 2000);
      // Broadcast to other tabs
      if (broadcastRef.current) {
        broadcastRef.current.postMessage({ type: 'chat_update', messages });
      }
      return () => clearTimeout(timer);
    }
  }, [messages]);

  // Cross-tab sync via BroadcastChannel
  useEffect(() => {
    try {
      broadcastRef.current = new BroadcastChannel('afrilink_chat');
      broadcastRef.current.onmessage = (event) => {
        if (event.data.type === 'chat_update') {
          setMessages(event.data.messages);
        }
        if (event.data.type === 'chat_reset') {
          setMessages(event.data.messages);
          setLiveSession(null);
        }
      };
    } catch(e) {}
    return () => { try { broadcastRef.current?.close(); } catch(e) {} };
  }, []);

  const prevMsgCount = useRef(messages.length);
  useEffect(() => {
    // Only auto-scroll when new messages are added, not on every render
    if (messages.length > prevMsgCount.current || isOpen) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
    prevMsgCount.current = messages.length;
  }, [messages.length, isOpen]);

  const startLiveChat = async () => {
    const API = window.location.hostname === 'localhost' ? 'http://localhost:4000' : 'https://api.afrilink.io';
    try {
      const res = await fetch(API + '/api/support/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: localStorage.getItem('afrilink_user_email') || 'anonymous',
          subject: 'Live support request',
          message: 'User requested to speak with a live agent.',
        }),
      });
      const data = await res.json();
      if (data.success) {
        setLiveSession(data.data.sessionId);
        setMessages(prev => [...prev,
          { role: 'bot', text: 'You are now connected to our support queue. An agent will respond shortly. You can type your message below.' },
        ]);
        // Start polling for agent replies
        setPolling(true);
        pollForReplies(data.data.sessionId);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'bot', text: 'Unable to connect to live support right now. Please email support@afrilink.io instead.' }]);
    }
  };

  const pollForReplies = (sessionId) => {
    const API = window.location.hostname === 'localhost' ? 'http://localhost:4000' : 'https://api.afrilink.io';
    let lastCheck = new Date().toISOString();
    const interval = setInterval(async () => {
      try {
        const res = await fetch(API + '/api/support/sessions/' + sessionId + '/messages?after=' + lastCheck);
        const data = await res.json();
        if (data.success && data.data.messages.length > 0) {
          const agentMsgs = data.data.messages.filter(function(m) { return m.sender === 'agent'; });
          if (agentMsgs.length > 0) {
            setMessages(prev => [...prev, ...agentMsgs.map(function(m) {
              return { role: 'bot', text: (m.senderName || 'Agent') + ': ' + m.text };
            })]);
          }
          lastCheck = data.data.messages[data.data.messages.length - 1].createdAt;
        }
        if (data.data.status === 'CLOSED') {
          clearInterval(interval);
          setPolling(false);
          setLiveSession(null);
          setMessages(prev => [...prev, { role: 'bot', text: 'This support session has been closed. Feel free to start a new one anytime.' }]);
        }
      } catch (e) { /* ignore polling errors */ }
    }, 5000);
    return () => clearInterval(interval);
  };

  const sendLiveMessage = async (text) => {
    const API = window.location.hostname === 'localhost' ? 'http://localhost:4000' : 'https://api.afrilink.io';
    try {
      await fetch(API + '/api/support/sessions/' + liveSession + '/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender: 'user',
          senderName: localStorage.getItem('afrilink_user_email') || 'User',
          text: text,
        }),
      });
    } catch (e) { /* ignore */ }
  };

  const handleSend = (text) => {
    const question = text || input;
    if (!question.trim()) return;

    const userMsg = { role: 'user', text: question };

    if (liveSession) {
      setMessages((prev) => [...prev, userMsg]);
      sendLiveMessage(question);
      setInput('');
      return;
    }

    const result = findAnswer(question);

    if (result.liveAgent) {
      setMessages((prev) => [...prev, userMsg, { role: 'bot', text: 'Connecting you to a live agent...' }]);
      setInput('');
      startLiveChat();
      return;
    }

    if (result.closeChat) {
      setMessages((prev) => [...prev, userMsg, { role: 'bot', text: result.a }]);
      setInput('');
      setTimeout(() => setIsOpen(false), 3000);
      return;
    }

    const botMsg = { role: 'bot', text: result.a, link: result.link, section: result.section, options: result.options };

    setMessages((prev) => [...prev, userMsg, botMsg]);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  const navigateToDoc = (link, section) => {
    if (section) {
      window.location.href = link + '?section=' + section;
    } else {
      window.location.href = link;
    }
  };

  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          style={{
            position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
            width: '56px', height: '56px', borderRadius: '50%',
            background: '#000', color: '#fff', border: 'none', cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'transform 0.2s',
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}

      {/* Chat window */}
      {isOpen && (
        <div
          onDragOver={(e) => { e.preventDefault(); setChatDragOver(true); }}
          onDragLeave={() => setChatDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setChatDragOver(false);
            const file = e.dataTransfer.files[0];
            if (file && ['image/jpeg','image/png','image/webp','application/pdf'].includes(file.type)) {
              setChatFile(file);
            } else if (file) {
              setMessages(prev => [...prev, { role: 'bot', text: 'Only JPEG, PNG, WebP and PDF files are supported.' }]);
            }
          }}
          style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
          width: '380px', maxWidth: 'calc(100vw - 48px)',
          height: '520px', maxHeight: 'calc(100vh - 48px)',
          borderRadius: '20px', overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column',
          background: '#fff', border: chatDragOver ? '2px solid #1D9E75' : '1px solid #e5e7eb',
        }}>
          {/* Header */}
          <div style={{
            background: '#000', color: '#fff', padding: '16px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '32px', height: '32px', borderRadius: '10px',
                background: '#1D9E75', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontWeight: '700',
              }}>AL</div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600' }}>AfriLink Support</div>
                <div style={{ fontSize: '11px', color: '#9ca3af' }}>Ask anything about our API</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => {
                const initial = [{ role: 'bot', text: "Hey! I'm AfriLink's assistant. What can I help you with?", options: ['How do I get started?', 'How does the Connect Widget work?', 'How do I verify a BVN?', 'What are the pricing plans?', 'How do I test in sandbox?', 'Talk to a human'] }];
                setMessages(initial);
                setLiveSession(null);
                localStorage.setItem('afrilink_chat_messages', JSON.stringify(initial));
                if (broadcastRef.current) broadcastRef.current.postMessage({ type: 'chat_reset', messages: initial });
                fetch(API + '/api/chat/transcript', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: getUserId(), messages: initial }) }).catch(() => {});
              }} title="Start over" style={{
                background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '14px',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
              </button>
              <button onClick={() => setIsOpen(false)} style={{
                background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '18px',
              }}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M4 4L14 14M14 4L4 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>

          {/* Drop overlay */}
          {chatDragOver && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(29,158,117,0.08)', zIndex: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '20px',
            }}>
              <div style={{ textAlign: 'center' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#1D9E75" strokeWidth="1.5" style={{ margin: '0 auto 8px' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" strokeLinecap="round" strokeLinejoin="round" />
                  <polyline points="17,8 12,3 7,8" strokeLinecap="round" strokeLinejoin="round" />
                  <line x1="12" y1="3" x2="12" y2="15" strokeLinecap="round" />
                </svg>
                <p style={{ fontSize: '13px', color: '#1D9E75', fontWeight: 600 }}>Drop file here</p>
              </div>
            </div>
          )}

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '16px',
            display: 'flex', flexDirection: 'column', gap: '12px',
          }}>
            {messages.map((msg, i) => (
              <div key={i} style={{
                alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
              }}>
                <div style={{
                  padding: '10px 14px',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: msg.role === 'user' ? '#000' : '#f3f4f6',
                  color: msg.role === 'user' ? '#fff' : '#111',
                  fontSize: '13px', lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                }}>
                  {msg.text}
                </div>
                {msg.options && msg.role === 'bot' && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                    {msg.options.map(function(opt) { return (
                      <button key={opt} onClick={function() { handleSend(opt); }} style={{
                        padding: '4px 10px', fontSize: '11px',
                        background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px',
                        cursor: 'pointer', color: '#374151', transition: 'background 0.15s',
                        whiteSpace: 'nowrap',
                      }}
                        onMouseOver={function(e) { e.currentTarget.style.background = '#f3f4f6'; }}
                        onMouseOut={function(e) { e.currentTarget.style.background = '#fff'; }}
                      >{opt}</button>
                    ); })}
                  </div>
                )}
                {msg.link && msg.role === 'bot' && (
                  <button
                    onClick={() => navigateToDoc(msg.link, msg.section)}
                    style={{
                      marginTop: '6px', fontSize: '11px', color: '#1D9E75',
                      background: '#ecfdf5', border: '1px solid #d1fae5',
                      borderRadius: '8px', padding: '4px 10px', cursor: 'pointer',
                    }}
                  >
                    View in docs &rarr;
                  </button>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '12px 16px', borderTop: '1px solid #e5e7eb',
          }}>
            {chatFile && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px',
                background: '#f3f4f6', borderRadius: '8px', marginBottom: '8px', fontSize: '12px',
              }}>
                <span style={{ color: '#374151' }}>{chatFile.name}</span>
                <span style={{ color: '#9ca3af' }}>({(chatFile.size / 1024).toFixed(1)} KB)</span>
                <button onClick={() => setChatFile(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer' }}>x</button>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => fileInputRef.current?.click()} title="Attach file" style={{
                width: '40px', height: '40px', borderRadius: '12px',
                background: '#f3f4f6', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              </button>
              <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.pdf,.webp" onChange={(e) => { if (e.target.files[0]) { setChatFile(e.target.files[0]); setMessages(prev => [...prev, { role: 'user', text: 'Attached: ' + e.target.files[0].name }, { role: 'bot', text: 'Got it! I can see you attached ' + e.target.files[0].name + '. To upload files to AfriLink, use the file upload API:\n\nPOST /api/files/upload\n\nSend as multipart/form-data with fields: file, userId, category (identity, selfie, document, address_proof, business).\n\nOr use the drag-and-drop upload component in your app.', link: '/docs', section: 'sandbox' }]); e.target.value = ''; } }} style={{ display: 'none' }} />
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about AfriLink..."
                style={{
                  flex: 1, padding: '10px 14px', fontSize: '13px',
                  border: '1px solid #e5e7eb', borderRadius: '12px',
                  outline: 'none',
                }}
              />
              <button onClick={() => handleSend()} style={{
                width: '40px', height: '40px', borderRadius: '12px',
                background: '#000', color: '#fff', border: 'none',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
